import { useMemo, useState } from 'react'
import { ArrowLeftRight, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useSwapAllocationVehicles } from '../../hooks/backend/fleet'
import { type VehicleGroup, type VehicleOption, vehicleLabel } from '../../utils/allocation'

// Exchanges the entire crew between two vehicles. Vehicle A is fixed to the card
// this button sits on; vehicle B is chosen from the other vehicles in the run.
export function SwapVehiclesDialog({
  vehicleA,
  otherVehicles,
  onSwapped,
}: {
  vehicleA: VehicleGroup
  otherVehicles: VehicleOption[]
  onSwapped: () => void
}) {
  const [open, setOpen] = useState(false)
  const [vehicleBId, setVehicleBId] = useState<string>('')
  const [reason, setReason] = useState('')
  const swap = useSwapAllocationVehicles()

  const reasonValid = reason.trim().length > 0
  const canSubmit = vehicleBId !== '' && reasonValid && !swap.loading

  const sortedOthers = useMemo(
    () => [...otherVehicles].sort((a, b) => a.plate.localeCompare(b.plate)),
    [otherVehicles],
  )

  function reset() {
    setVehicleBId('')
    setReason('')
  }

  async function handleSwap() {
    if (!canSubmit) return
    try {
      await swap.trigger({
        run_id: vehicleA.run_id,
        vehicle_a: vehicleA.vehicle_id,
        vehicle_b: vehicleBId,
        reason: reason.trim(),
      }).result
      toast.success('Cars swapped', {
        description: 'The whole crew has been exchanged between the two cars.',
      })
      setOpen(false)
      reset()
      onSwapped()
    } catch (err) {
      // Surface the message the DB function raised, verbatim.
      toast.error('Swap not done', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reset()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 w-full gap-1.5 px-2 text-xs">
          <ArrowLeftRight className="h-3.5 w-3.5" />
          Swap car
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Swap car</DialogTitle>
          <DialogDescription>
            Exchange the entire crew between {vehicleLabel(vehicleA)} and another vehicle. The driver
            pairs stay together; only the car changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Vehicle A
            </p>
            <p className="mt-0.5 font-mono font-semibold text-foreground">{vehicleA.plate}</p>
            <p className="text-xs text-muted-foreground">
              {[vehicleA.make, vehicleA.model].filter(Boolean).join(' ') || 'Unknown model'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="swap-vehicle-b">Swap with</Label>
            <Select value={vehicleBId} onValueChange={setVehicleBId}>
              <SelectTrigger id="swap-vehicle-b">
                <SelectValue placeholder="Choose a vehicle" />
              </SelectTrigger>
              <SelectContent>
                {sortedOthers.map((v) => (
                  <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                    {vehicleLabel(v)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="swap-reason">Reason</Label>
            <Textarea
              id="swap-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this crew changing vehicles? A driver must be able to be told."
              rows={3}
            />
            {!reasonValid && reason.length > 0 && (
              <p className="text-xs text-destructive">A reason cannot be blank.</p>
            )}
          </div>

          {swap.error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{swap.error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={swap.loading}>
            Cancel
          </Button>
          <Button onClick={handleSwap} disabled={!canSubmit} className="gap-1.5">
            {swap.loading ? <Spinner className="h-4 w-4" /> : <ArrowLeftRight className="h-4 w-4" />}
            Swap car
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
